// src/components/subscription/SubscriptionRequired.js
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import axios from "axios";
import Footer from '../footer/Footer'; 

const API_URL = process.env.REACT_APP_API_URL || "https://worthy-optimism-production.up.railway.app/api";

const SubscriptionRequired = () => {
  const [loading, setLoading] = useState(true);
  const [paymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [userStats, setUserStats] = useState({ queryCount: 0, queryLimit: 3, subscriptionType: "free" });
  const [needsSubscription, setNeedsSubscription] = useState(true);

  const { currentUser, token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        if (currentUser && token) {
          const config = { headers: { "Authorization": `Bearer ${token}` } };
          const statsResponse = await axios.get(`${API_URL}/subscription/stats`, config);
          const { queryCount = 0, subscriptionType, queryLimit } = statsResponse.data;
          setUserStats({ queryCount, subscriptionType, queryLimit: queryLimit === null && subscriptionType === "unlimited" ? Infinity : (queryLimit || 3) });

          if (subscriptionType && subscriptionType !== "free" && subscriptionType !== null) {
            setNeedsSubscription(false);
            setTimeout(() => navigate("/app/chat"), 100);
          } else if (queryCount < (statsResponse.data.queryLimit || 3) ) {
             setNeedsSubscription(false);
             setTimeout(() => navigate("/app/chat"), 100);
          } else {
            setNeedsSubscription(true);
          }
        } else {
          setNeedsSubscription(true);
          setUserStats({ queryCount: 0, queryLimit: 3, subscriptionType: "free" });
        }
      } catch (error) {
        console.error("SubscriptionRequired: Failed to fetch subscription data:", error);
        setNeedsSubscription(true);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [currentUser, token, navigate]);

  const handleManualPaymentRequest = (planId) => {
    if (!currentUser || !token) {
        setPaymentError("Molimo prijavite se da biste zatražili ručno plaćanje.");
        return;
    }
    // Redirect to bank instructions page
    navigate("/app/bank-instructions");
  };


  if (loading) {
    return (
      <div className="page-loading-container">
        <div className="loading-indicator">
          <div className="dots"><div className="dot"></div><div className="dot"></div><div className="dot"></div></div>
          <p>Učitavanje...</p>
        </div>
      </div>
    );
  }

  if (!needsSubscription && !loading) {
    return (
        <div className="page-loading-container">
            <div className="loading-indicator">
                <p>Preusmjeravanje na chat...</p>
            </div>
        </div>
    );
  }

  const plansData = [
    {
      id: "start",
      name: "Start plan",
      price: "49,00 KM",
      billing: "/mjesečno",
      features: [
        "Pristup AI chat funkcionalnosti",
        "50 upita mjesečno",
      ],
      popular: false,
    },
    {
      id: "pro",
      name: "Pro plan",
      price: "99,00 KM",
      billing: "/mjesečno",
      features: [
        "Pristup AI chat funkcionalnosti",
        "150 upita mjesečno",
        "Podrška putem e-maila",
        "Pristup novim funkcionalnostima",
      ],
      popular: true,
    },
    {
      id: "premium",
      name: "Premium plan",
      price: "199,00 KM",
      billing: "/mjesečno",
      features: [
        "Pristup AI chat funkcionalnosti",
        "500 upita mjesečno",
        "Prioritetna podrška",
        "Pristup novim funkcionalnostima",
      ],
      popular: false,
    },
  ];

  return (
    <div className="subscription-required-page">
      <div className="subscription-content">
        <div className="header-section">
          <h1>Potrebna Vam je Pretplata</h1>
          {!currentUser ? (
            <p className="subtitle">
              Prijavite se ili kreirajte račun kako biste dobili 3 besplatna pitanja ili se pretplatili za neograničeni pristup.
            </p>
          ) : (
            <p className="subtitle">
              Iskoristili ste {userStats.queryCount}/{userStats.queryLimit === Infinity ? "neograničeno" : userStats.queryLimit} dostupnih upita. Za nastavak korištenja AI chat funkcije, odaberite jedan od naših planova.
            </p>
          )}
        </div>

        {currentUser && (
          <div className="usage-overview-card">
            <h4>Pregled Vaše Trenutne Potrošnje</h4>
            <div className="progress-bar-container">
              <div 
                className="progress-bar-fill"
                style={{ width: `${Math.min((userStats.queryCount / (userStats.queryLimit === Infinity ? userStats.queryCount : userStats.queryLimit)) * 100, 100)}%` }}
              ></div>
            </div>
            <p>{userStats.queryCount} / {userStats.queryLimit === Infinity ? "∞" : userStats.queryLimit} iskorištenih upita</p>
            {userStats.subscriptionType !== "free" && userStats.subscriptionType !== null && userStats.queryLimit !== Infinity &&
                <p className="renewal-info">Vaš limit se obnavlja s Vašom pretplatom.</p>
            }
          </div>
        )}

        {paymentError && 
          <div className="payment-error-banner">
            <p>{paymentError}</p>
          </div>
        }

        <div className="plans-section">
          <h2>Odaberite Plan Koji Vam Najviše Odgovara</h2>
          <div className="plans-grid">
            {plansData.map((plan) => (
              <div key={plan.id} className={`plan-card ${plan.popular ? "popular" : ""}`}>
                {plan.popular && <div className="popular-badge">NAJPOPULARNIJE</div>}
                <h3>{plan.name}</h3>
                <div className="price-section">
                  <span className="price">{plan.price}</span>
                  <span className="billing-cycle">{plan.billing}</span>
                </div>
                <ul className="features-list">
                  {plan.features.map((feature, index) => (
                    <li key={index}>✓ {feature}</li>
                  ))}
                </ul>
                <div className="payment-options">
                  {/* <button
                    className={`select-plan-button online-payment ${plan.popular ? "popular-button" : ""}`}
                    onClick={() => handleOnlinePayment(plan.id)}
                    disabled={paymentLoading || !currentUser}
                  >
                    {paymentLoading ? "Obrada..." : "Plati Online"}
                  </button> */}
                  <button
                    className={`select-plan-button manual-payment ${plan.popular ? "popular-button-outline" : ""}`}
                    onClick={() => handleManualPaymentRequest(plan.id)}
                    disabled={paymentLoading || !currentUser}
                  >
                    Plati Uplatnicom
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {!currentUser && (
            <div className="auth-actions">
                <button onClick={() => navigate("/app/login")} className="auth-button-primary">Prijava</button>
                <button onClick={() => navigate("/app/register")} className="auth-button-secondary">Registracija</button>
            </div>
        )}

        <div className="footer-note">
          <p>Imate pitanja? <a href="mailto:fbrkovic@gmail.com">Kontaktirajte našu podršku</a>.</p>
          <Link to="/app/chat" className="back-link">Povratak na Chat</Link>
        </div>
      </div>

      <Footer />

    </div>
  );
};

export default SubscriptionRequired;
